import { type FormEvent, type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckSquare, Clock, GitBranch, Square, X } from 'lucide-react';
import {
  type ChecklistItem,
  type TaskDependency,
  type TaskTimeLog,
  addChecklistItem,
  addDependency,
  listChecklist,
  listDependencies,
  listTimeLogs,
  logTime,
  toggleChecklistItem,
} from '@/api/tasksExt';
import { useDirectory } from '@/hooks/useDirectory';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface Props {
  taskId: string;
  taskTitle: string;
  onClose: () => void;
}

type Tab = 'checklist' | 'dependencies' | 'time';

export function TaskDetailDrawer({ taskId, taskTitle, onClose }: Props) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('checklist');

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
      <div
        className="flex h-full w-full max-w-md flex-col border-l bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="task-detail-drawer"
      >
        <header className="flex items-start justify-between border-b px-4 py-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-[var(--color-lop-text-muted)]">
              {t('tasksExt.title')}
            </p>
            <h2 className="truncate text-lg font-semibold text-[var(--color-lop-text-primary)]">{taskTitle}</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-white/5" data-testid="task-detail-close">
            <X className="h-5 w-5 text-[var(--color-lop-text-muted)]" />
          </button>
        </header>

        <nav className="flex border-b">
          <TabBtn active={tab === 'checklist'} onClick={() => setTab('checklist')} testId="task-tab-checklist">
            <CheckSquare className="mr-1 inline h-4 w-4" />
            {t('tasksExt.tab.checklist')}
          </TabBtn>
          <TabBtn active={tab === 'dependencies'} onClick={() => setTab('dependencies')} testId="task-tab-deps">
            <GitBranch className="mr-1 inline h-4 w-4" />
            {t('tasksExt.tab.dependencies')}
          </TabBtn>
          <TabBtn active={tab === 'time'} onClick={() => setTab('time')} testId="task-tab-time">
            <Clock className="mr-1 inline h-4 w-4" />
            {t('tasksExt.tab.time')}
          </TabBtn>
        </nav>

        <div className="flex-1 overflow-y-auto p-4">
          {tab === 'checklist' && <ChecklistTab taskId={taskId} />}
          {tab === 'dependencies' && <DependenciesTab taskId={taskId} />}
          {tab === 'time' && <TimeTab taskId={taskId} />}
        </div>
      </div>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  testId,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testId: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`flex-1 border-b-2 px-2 py-2 text-xs font-medium ${
        active
          ? 'border-[var(--color-lop-accent)] text-[var(--color-lop-text-primary)]'
          : 'border-transparent text-[var(--color-lop-text-muted)] hover:text-[var(--color-lop-text-secondary)]'
      }`}
    >
      {children}
    </button>
  );
}

function ChecklistTab({ taskId }: { taskId: string }) {
  const { t } = useTranslation();
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    listChecklist(taskId)
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [taskId]);

  useEffect(() => {
    load();
  }, [load]);

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      await addChecklistItem(taskId, text.trim(), items.length);
      setText('');
      load();
    } finally {
      setBusy(false);
    }
  }

  async function toggle(item: ChecklistItem) {
    setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, completed: !i.completed } : i)));
    try {
      await toggleChecklistItem(item.id, !item.completed);
    } catch {
      load();
    }
  }

  const done = items.filter((i) => i.completed).length;

  return (
    <div className="space-y-3">
      {!loading && items.length > 0 && (
        <p className="text-xs text-[var(--color-lop-text-muted)]">
          {t('tasksExt.checklist.progress', { done, total: items.length })}
        </p>
      )}
      {loading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
      {!loading && items.length === 0 && (
        <p className="text-sm text-muted-foreground">{t('tasksExt.checklist.empty')}</p>
      )}
      <ul className="space-y-1">
        {items.map((item) => (
          <li key={item.id} className="flex items-center gap-2" data-testid="checklist-item">
            <button type="button" onClick={() => toggle(item)} className="shrink-0" data-testid="checklist-toggle">
              {item.completed ? (
                <CheckSquare className="h-4 w-4 text-emerald-400" />
              ) : (
                <Square className="h-4 w-4 text-[var(--color-lop-text-muted)]" />
              )}
            </button>
            <span className={`text-sm ${item.completed ? 'text-[var(--color-lop-text-muted)] line-through' : ''}`}>
              {item.text}
            </span>
          </li>
        ))}
      </ul>
      <form onSubmit={add} className="flex gap-2 pt-2">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('tasksExt.checklist.addPlaceholder')}
          data-testid="checklist-input"
        />
        <Button type="submit" size="sm" disabled={busy} data-testid="checklist-add">
          {t('tasksExt.add')}
        </Button>
      </form>
    </div>
  );
}

function DependenciesTab({ taskId }: { taskId: string }) {
  const { t } = useTranslation();
  const [deps, setDeps] = useState<TaskDependency[]>([]);
  const [loading, setLoading] = useState(true);
  const [dependsOn, setDependsOn] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    listDependencies(taskId)
      .then(setDeps)
      .catch(() => setDeps([]))
      .finally(() => setLoading(false));
  }, [taskId]);

  useEffect(() => {
    load();
  }, [load]);

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!dependsOn.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await addDependency(taskId, dependsOn.trim());
      setDependsOn('');
      load();
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      {loading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
      {!loading && deps.length === 0 && (
        <p className="text-sm text-muted-foreground">{t('tasksExt.dependencies.empty')}</p>
      )}
      <ul className="space-y-1">
        {deps.map((d) => (
          <li
            key={d.id}
            className="flex items-center gap-2 rounded border px-3 py-2 text-sm"
            data-testid="dependency-item"
          >
            <GitBranch className="h-4 w-4 shrink-0 text-[var(--color-lop-text-muted)]" />
            <span className="font-mono text-xs text-[var(--color-lop-text-secondary)]">
              {d.dependsOnTaskId.slice(0, 8)}
            </span>
            <span className="ml-auto rounded-full bg-zinc-800 px-2 py-0.5 text-[10px] text-zinc-300">{d.depType}</span>
          </li>
        ))}
      </ul>
      {error && <p className="text-xs text-red-400">{error}</p>}
      <form onSubmit={add} className="flex gap-2 pt-2">
        <Input
          value={dependsOn}
          onChange={(e) => setDependsOn(e.target.value)}
          placeholder={t('tasksExt.dependencies.addPlaceholder')}
          className="font-mono"
          data-testid="dependency-input"
        />
        <Button type="submit" size="sm" disabled={busy} data-testid="dependency-add">
          {t('tasksExt.add')}
        </Button>
      </form>
    </div>
  );
}

function TimeTab({ taskId }: { taskId: string }) {
  const { t } = useTranslation();
  const { nameFor } = useDirectory();
  const [logs, setLogs] = useState<TaskTimeLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [minutes, setMinutes] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    listTimeLogs(taskId)
      .then(setLogs)
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [taskId]);

  useEffect(() => {
    load();
  }, [load]);

  async function add(e: FormEvent) {
    e.preventDefault();
    const m = Number(minutes);
    if (!Number.isFinite(m) || m <= 0) return;
    setBusy(true);
    try {
      await logTime(taskId, m, notes.trim() || undefined);
      setMinutes('');
      setNotes('');
      load();
    } finally {
      setBusy(false);
    }
  }

  const total = logs.reduce((sum, l) => sum + l.minutes, 0);

  return (
    <div className="space-y-3">
      {!loading && logs.length > 0 && (
        <p className="text-xs text-[var(--color-lop-text-muted)]">
          {t('tasksExt.time.total', { hours: (total / 60).toFixed(1) })}
        </p>
      )}
      {loading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
      {!loading && logs.length === 0 && <p className="text-sm text-muted-foreground">{t('tasksExt.time.empty')}</p>}
      <ul className="space-y-1">
        {logs.map((l) => (
          <li key={l.id} className="rounded border px-3 py-2 text-sm" data-testid="time-log-item">
            <div className="flex items-center justify-between">
              <span className="font-medium text-[var(--color-lop-text-primary)]">
                {t('tasksExt.time.minutes', { count: l.minutes })}
              </span>
              <span className="text-xs text-[var(--color-lop-text-muted)]">{nameFor(l.userId)}</span>
            </div>
            {l.notes && <p className="mt-0.5 text-xs text-[var(--color-lop-text-secondary)]">{l.notes}</p>}
          </li>
        ))}
      </ul>
      <form onSubmit={add} className="space-y-2 pt-2">
        <div className="flex gap-2">
          <Input
            type="number"
            min="1"
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            placeholder={t('tasksExt.time.minutesPlaceholder')}
            className="w-32"
            data-testid="time-minutes"
          />
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('tasksExt.time.notesPlaceholder')}
            data-testid="time-notes"
          />
        </div>
        <Button type="submit" size="sm" disabled={busy} data-testid="time-add">
          {t('tasksExt.time.log')}
        </Button>
      </form>
    </div>
  );
}
